import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { assetUrl } from '@/utils/asset-url'
import { ReactComponent as IconChevronRight } from '@/assets/svgs/chevron-right.svg'
import Button from '@/components/Button/index'
import DataElementSharingModal2 from '@/components/DataElementSharingModal2/index'
import Loader from '@/components/DataTableLoader/index'
import Portal from '@/components/Portal'
import Tabs, { Tab } from '@/components/Tabs'
import Toast from '@/components/Toast'
import { featureConstant } from '@/constants/configuration/feature'
import { EnrolmentTypes } from '@/constants/use-case/enrolment'
import useDataElementEnrolments from '@/hooks/use-data-elements-enrolments/index'
import useModal from '@/hooks/use-modal'
import usePage from '@/hooks/use-page'
import usePrincipals from '@/hooks/use-principal/index'
import useToast from '@/hooks/use-toast'
import useUseCases from '@/hooks/use-use-cases/index'
import { useLocation, useNavigate, useParams } from '@/lib/router-dom'
import { feature } from '@/utils/feature-verifier'
import { getPrincipalCount } from '@/utils/principal-storage'
import { getUseCaseId } from "@/utils/use-case-storage"
import { useEffect, useMemo, useState } from 'react'
// [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR.
// import Breadcrumbs from '@/componentsDS/Breadcrumbs/index'
import Breadcrumb from '@/dsComponents/Breadcrumb';
import { Box } from '@dextech-ai/dex-design-system';
const ViewDataElements = () => {

	/*=================================================
		General
	/================================================*/
	const { useCaseId } = useParams()
	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2)

	const pageTitle = 'Data Elements Exchanged'
	const subPageTitle = useCase.name

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label:'',
				link:'/'
			},
			{
				label:'Use Cases',
				link:'/use-cases/enrolment'
			},
			{
				label: useCase?.name,
				link: `/use-cases/${useCaseId}/dashboard`
			},
			{
				label: pageTitle,
				link: ``
			},
		]

	 })

	const { goTo } = useNavigate()

	const { currentState } = useLocation()

	const [currentPage, setCurrentPage] = useState(1)
	const [principalCount, setPrincipalCount] = useState(getPrincipalCount())
	const pageLimit = 10

	const { find: findPrincipals, loading: loadingPrincipals } = usePrincipals()

	const findPrincipalCount = async () => {
		if (principalCount > 0) return principalCount;
		if (loadingPrincipals) return;

		const principalItems = await findPrincipals()
		setPrincipalCount(principalItems?.length ?? 0)
	}

	useEffect(() => {
		findPrincipalCount();
	}, [])

	const toastType = {
		success: "success",
		error: "error"
	}

	const [sortConfig, setSortConfig] = useState({
		sortBy: 'dataElement.dataElementName',
		direction: true,
	})

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { items: dataElementsItems, meta, fetch, error, loading: loadingDataElements } = useDataElementEnrolments()
	const { isAgentPrincipalEnabled } = useUseCases()

	useEffect(() => {
		addToastMessage(error, toastType.error)
	}, [error])

	/*=================================================
		Sort
	/================================================*/

	function handleSort(sortBy) {
		let direction = sortConfig.direction

		if (sortConfig.sortBy === sortBy) {
			direction = !direction
		} else {
			direction = true
		}

		setSortConfig({ sortBy, direction })
	}

	/*=================================================
		Pagination
	/================================================*/

	function goToPrev() {
		if (currentPage === 1) {
			return
		}

		setCurrentPage((prev) => prev - 1)
	}

	function goToNext() {
		if (currentPage === meta.pageCount) {
			return
		}

		setCurrentPage((prev) => prev + 1)
	}

	useEffect(() => {
		if (dataElementsItems?.length) {
			let index = tabsItems.findIndex(data => data.active === true);
			fetch({
				currentPage: currentPage,
				pageSize: pageLimit,
				type: tabsItems[index].key,
				useCaseId: useCaseId,
				sortBy: sortConfig.key,
				order: sortConfig.direction === true ? "ASC" : "DESC"
			})
		}
	}, [currentPage, sortConfig])

	useEffect(() => {
		if (currentPage > meta?.pageCount) {
			setCurrentPage(meta?.pageCount || 1)
		}
	}, [dataElementsItems])

	/*=================================================
		Tabs
	/================================================*/

	const [elementsBtns, setElementsBtn] = useState([
		{
			label: 'Sharing',
			active: true,
			id: 'SHARING',
			tabs: [{
				key: EnrolmentTypes.provider,
				label: 'Contribute',
				active: true,
				options: [],
				customColumns: ['Contributing Pitstop(s)', 'Consumers'],
			},
			{
				key: EnrolmentTypes.provideOnBehalf,
				label: 'Provide On Behalf',
				active: false,
				options: [],
				customColumns: ['Providing Pitstop(s)', 'Providing on behalf of'],
			},
			{
				key: 'PROVIDE_ON_BEHALF_OF_PRINCIPAL',
				label: 'Provide On Behalf (Principal)',
				active: false,
				options: [],
				customColumns: ['Providing Pitstop(s)', 'Providing on behalf of (Principal)'],
			}
		],
		},
		{
			label: 'Receiving',
			active: false,
			id: 'RECEIVING',
			tabs: [{
				key: EnrolmentTypes.consumer,
				label: 'Consume',
				active: true,
				options: [],
				customColumns: ['Consuming Pitstop(s)', 'Contributors'],
			},
			{
				key: EnrolmentTypes.consumeOnBehalf,
				label: 'Consume On Behalf',
				active: false,
				options: [],
				customColumns: ['Consuming Pitstop(s)', 'Consuming on behalf of'],
			},
			{
				key: 'CONSUME_ON_BEHALF_OF_PRINCIPAL',
				label: 'Consume On Behalf (Principal)',
				active: false,
				options: [],
				customColumns: ['Consuming Pitstop(s)', 'Consuming on behalf of (Principal)'],
			}]
		},
	]);

	function filterDataElementTabs(tabs) {
		if(!isAgentPrincipalPhase2Enabled) {
			tabs = tabs.filter(tab => tab.key !== 'PROVIDE_ON_BEHALF_OF_PRINCIPAL')
		}
		return tabs?.filter(s => isAgentPrincipalEnabled(useCase?.agentStatus) ? true : !s?.key?.includes('PRINCIPAL')) ?? tabs
	}

	const [tabsItems, setTabsItems] = useState(filterDataElementTabs(elementsBtns[0].tabs))

	useEffect(() => {
		const { type, chip } = currentState ?? {}
		let selectedTab;
		if (type && chip) {
			const selectedElementBtn = elementsBtns.find(s => s.id === chip)
			setElementsBtn(elementsBtns.map(s => {
				s.active = s.id === chip
				return s
			}))
			selectedTab = selectedElementBtn.tabs.find(s => s.key === type)
			setTabsItems(filterDataElementTabs(selectedElementBtn.tabs).map(s => {
				s.active = s.key === type
				return s
			}))
		} else {
			selectedTab = tabsItems?.find(s => s.active === true)
		}
		fetch({ currentPage: 1, type: selectedTab?.key, useCaseId: useCaseId, pageSize: pageLimit, })
	}, [useCase])

	function selectElementBtn(index) {
		const btns = [...elementsBtns].map(item => ({ ...item, active: false }))
		btns[index].active = true

		setElementsBtn(btns)

		setTabsItems(filterDataElementTabs(elementsBtns[index].tabs));
		fetch({ currentPage: 1, type: elementsBtns[index].tabs[0].key, useCaseId: useCaseId, pageSize: pageLimit, })
	}

	async function selectTab(index) {
		const items = [...tabsItems].map((item) => ({ ...item, active: false }))

		items[index].active = true
		await fetch({
			currentPage: 1,
			pageSize: pageLimit,
			type: items[index].key,
			useCaseId: useCaseId
		})
		setCurrentPage(1)
		setTabsItems(items)
	}

	function handleCurrentInput(e) {
		const value = e.target?.value === '' ? 1 : e.target.value
		const current = parseInt(value)

		if (current < 1) {
			return setCurrentPage(1)
		}

		setCurrentPage(current)
	}

	/*=================================================
		Modal
	/================================================*/

	const { visible: initiateDataElementModal, show: showInitiateDataElementModal, hide: hideInitiateDataElementModal } = useModal()

	/*=================================================
		Navigation
	/================================================*/

	const goToDataElementDetail = (item) => {
		let type = tabsItems.find(i => i.active)?.key;

		goTo(`/use-cases/${useCaseId}/data-elements/${item.dataElement.dataElementId}/${type.toLowerCase()}`)
	}

	/*=================================================
		Data Table
	/================================================*/
	function getClassName(name) {
		if (!sortConfig) {
			return
		}

		const sortConfigCheck = sortConfig.direction === true ? 'is-asc' : 'is-desc'
		return sortConfig.sortBy === name ? sortConfigCheck : '';
	}

	const DataTable = ({ data }) => {
		const list = dataElementsItems.map((item, index) => {
			const SYS_TO_SHOW = 1;
			const ORGS_TO_SHOW = 4;
			return (
				<div className="data-table__table__body__row" key={index}>
					<div className="data-table__table__body__content large data-table__table__body__content--text data-table__table__body__content--heavy data-table__table__body__content--break data-table__flex">
						{item?.dataElement?.dataElementName}
					</div>
					<div className="data-table__table__body__content data-table__table__body__content--text">
						{item?.system?.slice(0, SYS_TO_SHOW)?.map((system) => system?.name).join(', ')}
						{item?.system?.length > SYS_TO_SHOW && <>  +{item?.system?.length - SYS_TO_SHOW}</>}
					</div>
					<div className="data-table__table__body__content data-table__table__body__content--text">
						<div className="layout-dashboard__content__avatar__list">
							{item?.participants?.length > 0 &&
								item?.participants?.map((participant, index) => {
									return (
										index < ORGS_TO_SHOW && (
											<div
												key={index}
												className="layout-dashboard__content__avatar"
												style={{
													backgroundImage: `url(${participant?.iconUrl})`,
												}}
											></div>
										)
									)
								})}
							{item?.participants?.length > ORGS_TO_SHOW && (
								<div className="layout-dashboard__content__center">
									&nbsp;+{item?.participants?.length - ORGS_TO_SHOW}
								</div>
							)}
						</div>
					</div>
					<div className={`data-table__table__body__content data-table__table__body__content--end ${isAgentPrincipalPhase2Enabled ? '' : 'data-table__table__body__content--text'}`}>
						<div
							data-testid="viewDetail-btn"
							className="layout-dashboard__content__action"
							onClick={() => {
								goToDataElementDetail(item)
							}}
						>
							<IconChevronRight />
						</div>
					</div>
				</div>
			)
		})

		const selectedTab = tabsItems.find(i => i.active);
		return (
			<div className="data-table__content">
				<div className="data-table__content__cols">
					<div className="data-table__content__col">
						<div className="data-table__table">
							<div className="data-table__table__head">
								<div className="data-table__table__head__row">
									{selectedTab && (
										<>
											<div
												className={`data-table__table__head__title large data-table__table__head__title__sort-order ${getClassName(
													'dataElement.dataElementName',
												)}`}
												id="dataElement"
												onClick={(e) => {
													handleSort('dataElement.dataElementName')
												}}
											>
												Data Element
												<span className="data-table__table__head__icon__sort-order"></span>
											</div>
											<div
												className={`data-table__table__head__title data-table__table__head__title__sort-order`}
												id="providingPitstop"
											>
												{selectedTab.customColumns[0]}
												<span className="data-table__table__head__icon__sort-order"></span>
											</div>
											<div
												className={`data-table__table__head__title data-table__table__head__title__sort-order`}
												id="providingBehalf"
											>
												{selectedTab.customColumns[1]}
												<span className="data-table__table__head__icon__sort-order"></span>
											</div>
										</>
									)}
									<div className="data-table__table__head__title data-table__header data-table__table__head__title--end"></div>
								</div>
							</div>

							<Loader loading={loadingDataElements}>
								{data.length > 0 ? (
									<div className="data-table__table__body">{list}</div>
								) : (
									<div className="data-table__empty">There's no data found</div>
								)}
								{data.length > 0 && (
									<div className="data-table__table__footer">
										<div className="data-table__table__footer__row">
											<div className="data-table__table__footer__row__left">
												<div className="data-table__table__pagination">
													<input
														data-testid="pagination-input"
														className="data-table__table__pagination__page"
														type="number"
														value={currentPage}
														onChange={(e) => handleCurrentInput(e)}
														min="1"
													/>
													<span className="data-table__table__pagination__page__meta">
														{' '}
														of {meta?.pageCount || 1} pages{' '}
													</span>
													<button
														data-testid="pagination-prev"
														className="data-table__table__pagination__prev"
														onClick={() => goToPrev()}
														disabled={currentPage === 1}
													>
														<IconChevronDown />
													</button>
													<button
														data-testid="pagination-next"
														className="data-table__table__pagination__next"
														onClick={() => goToNext()}
														disabled={
															currentPage === meta?.totalCount
														}
													>
														<IconChevronDown />
													</button>
												</div>
											</div>
											<div className="data-table__table__footer__row__right">
												<div className="data-table__table__meta">
													Showing {dataElementsItems?.length} entries
												</div>
											</div>
										</div>
									</div>
								)}
							</Loader>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<>
			{/* [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR. */}
			{/* <Breadcrumbs breadcrumbItems={breadcrumbsItems} /> */}
			<Breadcrumb items={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">Data Elements Exchanged</div>
				</div>
				<div className="title-header__right">
					<Button round full maxWidth={140} text="Add Data Elements" submit={() => { showInitiateDataElementModal() }} />
				</div>
			</div>

			<div className="data_element_list__chips">
				{elementsBtns.map((item, index) => (
					<Button key={item.id} text={item.label} ascent selected={item.active} sizing='mini' round submit={() => {
						selectElementBtn(index)
					}} />
				))}
			</div>

			<Tabs>
				{tabsItems.map((item, index) => (
					<Tab
						key={index}
						label={item.label}
						active={item.active}
						onClick={() => selectTab(index)}
					/>
				))}
			</Tabs>
			<hr style={{ margin: 0 }} />

			<div className="overview">
				<Box minH="540px" mt={4}>
					<DataTable data={dataElementsItems || []} />
				</Box>
			</div>

			<DataElementSharingModal2
				isVisible={initiateDataElementModal}
				hideModal={hideInitiateDataElementModal}
				useCaseId={useCaseId}
				useCaseAgentStatus={useCase.agentStatus}
				principalCount={principalCount}
			/>

			<Toast toastList={toastList} />
		</>
	)
}

export default ViewDataElements
